import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { UploadCloud } from 'lucide-react';
import { useDocumentsApi, DocumentDTO } from '../../../hooks/useDocumentsApi';
import { ConfirmUploadDialog } from '../ConfirmUploadDialog/ConfirmUploadDialog';
import { AnalysisInProgressDialog } from '../AnalysisInProgressDialog/AnalysisInProgressDialog';
import { SelectDocumentTypeDialog } from '../SelectDocumentTypeDialog/SelectDocumentTypeDialog';
import { SpecifyTaxDocumentTypeDialog } from '../SpecifyTaxDocumentTypeDialog/SpecifyTaxDocumentTypeDialog';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { wsManager } from '../../../lib/websocket';

interface AddDocumentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: (doc: DocumentDTO) => void;
  onProcessingStarted?: () => void;
  onError?: (message: string) => void;
}

export const AddDocumentDialog: React.FC<AddDocumentDialogProps> = ({
  isOpen,
  onClose,
  onCompleted,
  onProcessingStarted,
  onError,
}) => {
  const api = useDocumentsApi();
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const [isSelectTypeOpen, setIsSelectTypeOpen] = useState(false);
  const [isSpecifyTaxTypeOpen, setIsSpecifyTaxTypeOpen] = useState(false);
  const [uploadedDocument, setUploadedDocument] = useState<DocumentDTO | null>(null);
  const [classifiedType, setClassifiedType] = useState<string | null>(null);

  // Subscribe to WebSocket classification events.
  // When the BFF async pipeline classifies the document, it broadcasts
  // `zuroq.document.classified` via WS. We update the suggestedType.
  useEffect(() => {
    const unsub = wsManager.on('zuroq.document.classified', (data: unknown) => {
      const event = data as {
        documentId?: string;
        documentType?: string;
      };
      if (uploadedDocument && event.documentId === uploadedDocument._id && event.documentType) {
        setClassifiedType(event.documentType);
      }
    });
    return () => {
      unsub();
    };
  }, [uploadedDocument]);

  // Ensure WS is connected when dialog opens
  useEffect(() => {
    if (isOpen) {
      wsManager.connect();
    }
  }, [isOpen]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);
      setSelectedFiles(files);
      onClose();
      setIsConfirmOpen(true);
    }
  };

  const handleConfirm = async () => {
    if (selectedFiles.length === 0) return;

    setIsConfirmOpen(false);
    setIsAnalysisOpen(true);
    toast.loading(t('toast.upload.start'), { id: 'upload' });

    try {
      const uploadedDoc = await api.upload(selectedFiles[0]);
      toast.success(t('toast.upload.success'), { id: 'upload' });
      // Backend now returns the AI-detected documentType synchronously.
      // Seed classifiedType from the backend response; WS may override later.
      setClassifiedType(uploadedDoc.documentType || null);
      setUploadedDocument(uploadedDoc);
      setIsAnalysisOpen(false);
      setIsSelectTypeOpen(true);
      toast(t('toast.type.prompt'), { icon: '🧠' });
    } catch (error) {
      console.error('Upload failed:', error);
      setIsAnalysisOpen(false);
      toast.error(t('toast.upload.error'), { id: 'upload' });
      // Optionally, show an error message to the user
    }
  };

  const handleReturn = () => {
    setIsConfirmOpen(false);
  };

  const handleTypeSelect = async (type: string, subType?: string) => {
    if (!uploadedDocument) return;

    if (type === 'TAX_FORM' && !subType) {
      setIsSelectTypeOpen(false);
      setIsSpecifyTaxTypeOpen(true);
      return;
    }

    try {
      let code = type;
      if (type === 'TAX_FORM' && subType) {
        code = subType as any;
      }

      // Close UI and notify refresh immediately
      setIsSelectTypeOpen(false);
      setIsSpecifyTaxTypeOpen(false);
      onProcessingStarted?.();
      setUploadedDocument(null);
      setSelectedFiles([]);
      onClose();
      toast.loading(t('toast.processing.start'), { id: 'process' });

      const updated = await api.process((uploadedDocument as DocumentDTO)._id, code);
      toast.success(t('toast.processing.success'), { id: 'process' });
      onCompleted?.(updated);
    } catch (error: any) {
      console.error('Failed to update document type:', error);
      toast.error(t('toast.processing.error'), { id: 'process' });
      onError?.(t('toast.processing.error'));
    }
  };

  const handleTaxTypeBack = () => {
    setIsSpecifyTaxTypeOpen(false);
    setIsSelectTypeOpen(true);
  };

  const handleTaxTypeConfirm = (subType: string) => {
    handleTypeSelect('Documents Fiscaux', subType);
  };

  const handleApproval = async () => {
    if (!uploadedDocument) return;
    try {
      const type = uploadedDocument.documentType || classifiedType || 'OTHER';

      // Close UI and notify refresh immediately
      setIsSelectTypeOpen(false);
      onProcessingStarted?.();
      const current = uploadedDocument;
      setUploadedDocument(null);
      setSelectedFiles([]);
      onClose();
      toast.loading(t('toast.processing.start'), { id: 'process' });

      const updated = await api.process(current._id, type);
      toast.success(t('toast.processing.success'), { id: 'process' });
      onCompleted?.(updated);
    } catch (e: any) {
      console.error('Failed to process document:', e);
      toast.error(t('toast.processing.error'), { id: 'process' });
      onError?.(t('toast.processing.error'));
    }
  };

  // Fallback: if popup is closed before choosing a type but backend
  // already suggested one (via sync response or WS), auto-process.
  React.useEffect(() => {
    const suggested = classifiedType || uploadedDocument?.documentType;
    if (!isSelectTypeOpen && uploadedDocument && suggested && suggested !== 'OTHER') {
      (async () => {
        try {
          toast.loading(t('toast.processing.start'), { id: 'process' });
          const updated = await api.process(uploadedDocument._id, suggested);
          toast.success(t('toast.processing.success'), { id: 'process' });
          onCompleted?.(updated);
        } catch (err) {
          toast.error(t('toast.processing.error'), { id: 'process' });
          onError?.(t('toast.processing.error'));
        } finally {
          setUploadedDocument(null);
          setSelectedFiles([]);
          setClassifiedType(null);
        }
      })();
    }
  }, [isSelectTypeOpen, classifiedType]);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Téléverser des documents</DialogTitle>
            <DialogDescription>
              Glissez-déposez ou sélectionnez les fichiers à analyser.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center w-full">
            <label
              htmlFor="dropzone-file"
              className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <UploadCloud className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" />
                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                  <span className="font-semibold">Glissez-déposez vos documents ici</span>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Ou cliquez pour parcourir vos fichiers
                </p>
              </div>
              <input
                id="dropzone-file"
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmUploadDialog
        isOpen={isConfirmOpen}
        files={selectedFiles}
        onClose={handleReturn}
        onConfirm={handleConfirm}
      />

      <AnalysisInProgressDialog isOpen={isAnalysisOpen} onClose={() => setIsAnalysisOpen(false)} />

      <SelectDocumentTypeDialog
        isOpen={isSelectTypeOpen}
        onClose={() => setIsSelectTypeOpen(false)}
        onSelect={handleTypeSelect}
        suggestedType={
          (classifiedType && classifiedType !== 'OTHER' ? classifiedType : undefined) ||
          (uploadedDocument?.documentType && uploadedDocument.documentType !== 'OTHER'
            ? uploadedDocument.documentType
            : undefined)
        }
        onApprove={handleApproval}
      />

      <SpecifyTaxDocumentTypeDialog
        isOpen={isSpecifyTaxTypeOpen}
        onClose={() => setIsSpecifyTaxTypeOpen(false)}
        onBack={handleTaxTypeBack}
        onConfirm={handleTaxTypeConfirm}
      />
    </>
  );
};
